import { useMemo, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import NiceModal from "@ebay/nice-modal-react";
import { useTranslation } from "react-i18next";
import api from "../../../utils/api";
import { successToast, errorToast } from "../../../utils/toasts";
import CustomTag from "../../../components/core/blocks/CustomTag";
import BulkEditModal from "../../../components/core/basic/BulkEditModal";
import { FaPlus } from "react-icons/fa6";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { css } from "../../../../styled-system/css";
import { useModuleEnabled } from "../../../redux/selectors/modules";
import { EntityTagsFormModal } from "../../tags/components/EntityTagsFormModal";
import { EntityTagList } from "../../tags/components/EntityTagList";
import { invalidateAdQueries } from "../../perf_reports/api/ads/invalidateAdQueries";

const AdTags = ({ adPlanData }) => {
  const { t } = useTranslation(["advertising"]);
  const [showModal, setShowModal] = useState(false);
  const tagsEnhancedEnabled = useModuleEnabled("TAGS_ENHANCED");

  const queryClient = useQueryClient();

  const existingTagIds = useMemo(() => (adPlanData?.tags_enhanced ?? []).map((t) => t.id), [adPlanData?.tags_enhanced]);

  const updateTags = useMutation({
    mutationFn: async (updatedTags) => {
      return await api.patch(`/ads/${adPlanData.id}/`, {
        tags: [...new Set(updatedTags)],
      });
    },
    onSuccess: () => {
      successToast({ message: t(($) => $.advertising["Media Plan Tags Successfully Updated"]) });
      invalidateAdQueries(queryClient);
      setShowModal(false);
    },
    onError: (error) => {
      errorToast({ message: t(($) => $.advertising["Failed to Update Media Plan Tags"]), error });
      invalidateAdQueries(queryClient);
    },
  });

  const removeTag = (tagId) => {
    updateTags.mutate(existingTagIds.filter((id) => id !== tagId));
  };

  const openAddModal = () => {
    void NiceModal.show(EntityTagsFormModal, {
      entityType: "ADVERTISEMENT",
      title: t(($) => $.advertising["Edit Media Plan Tags"]),
      initialTagIds: existingTagIds,
      onSubmit: (tagIds) => {
        updateTags.mutate(tagIds);
      },
    });
  };

  if (tagsEnhancedEnabled) {
    return (
      <div className="py-5 text-center fs-standard d-flex align-items-center">
        <EntityTagList
          entityType="ADVERTISEMENT"
          tagIds={existingTagIds}
          onRemove={removeTag}
          ariaLabel={t(($) => $.advertising["Media plan tags"])}
        />
        <HoverInfo icon={<FaPlus className={css({ cursor: "pointer", marginLeft: "small" })} onClick={openAddModal} />}>
          {t(($) => $.advertising["Add custom media plan tags"])}
        </HoverInfo>
      </div>
    );
  }

  return (
    <div className="py-5 text-center fs-standard d-flex align-items-center">
      {adPlanData?.tags?.map((tag) => (
        <CustomTag
          taggedItemDescription="media plan"
          tag={tag}
          key={tag}
          onUpdate={(tag) => {
            const updatedTags = adPlanData?.tags?.filter((t) => tag.toLowerCase() !== t.toLowerCase());
            updateTags.mutate(updatedTags ?? []);
          }}
        />
      ))}

      <HoverInfo icon={<FaPlus className={css({ cursor: "pointer" })} onClick={() => setShowModal(true)} />}>
        {t(($) => $.advertising["Add custom media plan tags"])}
      </HoverInfo>

      <BulkEditModal
        title={t(($) => $.advertising["Edit Media Plan Tags"])}
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        defaultValues={adPlanData?.tags ?? []}
        onSave={(values) => {
          updateTags.mutate(values);
        }}
      />
    </div>
  );
};

export default AdTags;
